import { AnyAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { concat, filter, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { mapPayload } from '../../../../Utils/RxjsOperators/MapPayload';
import { MoonListManager } from '../../../MoonListManager/MoonListManager';
import { UserManager } from '../../../UserManager/UserManager';
import ComponentStateActions from '../../Global/ComponentState/ComponentStateActions';
import { StoreDependencies } from '../../Global/StoreDependencies';
import { StoreState } from '../../Global/StoreState';
import UserStoreActions from '../../Global/User/UserStoreActions';
import WidgetFrontActions from '../WidgetFront/WidgetFrontActions';
import MoonListActions from './MoonListActions';

const componentStatePrefix: string = 'MOON_LIST';

function getMoonListWithId(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(MoonListActions.getMoonListWithId.match),
    mapPayload(),
    switchMap((moonListId) =>
      concat(
        of(ComponentStateActions.setLoading(componentStatePrefix)),
        getMoonList(moonListId, dependencies.moonListManager!),
        of(ComponentStateActions.setSuccess(componentStatePrefix))
      )
    )
  );
}

function startRunWithMoonListId(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(MoonListActions.startRunWithMoonListId.match),
    mapPayload(),
    switchMap((moonListId) =>
      concat(
        of(ComponentStateActions.setCustom(componentStatePrefix, 'run/loading')),
        startRun(moonListId, dependencies.userManager!),
        of(ComponentStateActions.setCustom(componentStatePrefix, 'run/success')),
        of(MoonListActions.resetState())
      )
    )
  );
}

function deleteMoonListWithId(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(MoonListActions.deleteMoonListWithId.match),
    mapPayload(),
    switchMap((moonListId) =>
      concat(
        of(ComponentStateActions.setCustom(componentStatePrefix, 'delete/loading')),
        deleteMoonList(moonListId, dependencies.moonListManager!),
        of(ComponentStateActions.setCustom(componentStatePrefix, 'delete/success'))
      )
    )
  );
}

function getMoonList(moonListId: string, moonListManager: MoonListManager): Observable<AnyAction> {
  return moonListManager.getMoonListWithId(moonListId).pipe(map(MoonListActions.setMoonList));
}

function startRun(moonListId: string, userManager: UserManager): Observable<AnyAction> {
  return userManager
    .startRunWithMoonListId(moonListId)
    .pipe(switchMap((run) => [UserStoreActions.setCurrentUserRun(run), WidgetFrontActions.updateRun(run)]));
}

function deleteMoonList(moonListId: string, moonListManager: MoonListManager): Observable<AnyAction> {
  return moonListManager
    .deleteMoonListWithId(moonListId)
    .pipe(mergeMap(() => [MoonListActions.setMoonList(undefined), UserStoreActions.getCurrentUserMoonLists()]));
}

export default combineEpics(getMoonListWithId, startRunWithMoonListId, deleteMoonListWithId);
